import { useMemo } from 'react'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { formatTimestamp } from '@/lib/utils'
import { useOpenSignedDocument } from '@/hooks/api/useDocumentSignedUrl'
import { usePendingGateRevocations } from '@/hooks/usePendingGateRevocations'
import { useKycPlayerGates } from '@/hooks/chain/useKycPlayerGates'
import { GATE_LABELS, GATE_TYPES, type GateRecord, type GateType } from '@/types/player-detail'

const SORTED_GATE_TYPES = [...GATE_TYPES].sort((a, b) =>
    GATE_LABELS[a].localeCompare(GATE_LABELS[b]),
)

interface Props {
    gates: GateRecord[]
    address: string
    chainId: number
}

export function GatesTab({ gates, address, chainId }: Props) {
    const evidenceByType = useMemo(() => {
        const map = new Map<GateType, GateRecord>()
        for (const g of gates) map.set(g.gateType, g)
        return map
    }, [gates])
    const { pending } = usePendingGateRevocations()
    const eligibility = useKycPlayerGates(chainId, address)
    const bitmap = eligibility.data?.gates ?? 0n
    const chainLoading = eligibility.isLoading && !eligibility.data

    return (
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead>Gate</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Passed</TableHead>
                    <TableHead>Evidence</TableHead>
                    <TableHead>Set by</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {SORTED_GATE_TYPES.map((type) => {
                    const bitIndex = GATE_TYPES.indexOf(type)
                    const isSetOnChain = bitIndex >= 0 && ((bitmap >> BigInt(bitIndex)) & 1n) === 1n
                    return (
                        <GateRow
                            key={type}
                            type={type}
                            evidence={evidenceByType.get(type)}
                            isSetOnChain={isSetOnChain}
                            chainLoading={chainLoading}
                            revokeQueued={pending.has(type)}
                        />
                    )
                })}
            </TableBody>
        </Table>
    )
}

function GateRow({
    type,
    evidence,
    isSetOnChain,
    chainLoading,
    revokeQueued,
}: {
    type: GateType
    evidence: GateRecord | undefined
    isSetOnChain: boolean
    chainLoading: boolean
    revokeQueued: boolean
}) {
    const { open, loading, error } = useOpenSignedDocument()

    return (
        <TableRow>
            <TableCell className="font-medium">{GATE_LABELS[type]}</TableCell>
            <TableCell>
                {revokeQueued ? (
                    <Badge variant="inProgress" title="Revoke queued — confirming on-chain">
                        Revoke queued
                    </Badge>
                ) : chainLoading ? (
                    <Skeleton className="h-5 w-16" />
                ) : isSetOnChain ? (
                    <Badge variant="positive">Passed</Badge>
                ) : (
                    <Badge variant="muted">Not set</Badge>
                )}
            </TableCell>
            <TableCell className="text-muted-foreground text-xs">
                {formatTimestamp(evidence?.passedAt)}
            </TableCell>
            <TableCell>
                {evidence?.evidenceRef ? (
                    evidence.evidenceProvider === 'sumsub' ? (
                        <div className="flex flex-col gap-1">
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => open(evidence.evidenceRef!)}
                                disabled={loading}
                            >
                                {loading ? 'Opening…' : 'Open evidence'}
                            </Button>
                            {error && <span className="text-destructive text-xs">{error}</span>}
                        </div>
                    ) : (
                        <div className="flex flex-col">
                            <code className="text-xs">{evidence.evidenceRef}</code>
                            <span className="text-muted-foreground text-[10px]">
                                {evidence.evidenceProvider}
                            </span>
                        </div>
                    )
                ) : (
                    <span className="text-muted-foreground text-xs">—</span>
                )}
            </TableCell>
            <TableCell className="text-xs">
                <div>{evidence?.setBy ?? '—'}</div>
                <div className="text-muted-foreground">{formatTimestamp(evidence?.setAt)}</div>
            </TableCell>
        </TableRow>
    )
}
